import { Budget } from '../types/budget';
import { BUDGET_DATA_UPDATED_EVENT } from '../constants/events';
import {
  CategoryItem,
  ResponsibleItem,
  DestinationItem,
  ContributionCalculationType,
} from '../types/budget';
import { DEFAULT_CURRENCY } from '../constants/currency';
import i18n from '../i18n';
import { normalizeResponsible, normalizeDestination } from '../utils/normalization';
import { 
  createVersion, 
  initializeVersioning, 
  cleanupVersioningForBudget,
  getCurrentVersion,
  goToPreviousVersion,
  goToNextVersion,
  getVersionNavigationInfo,
  isViewingHistoricalVersion,
  undoLastChange,
  redoLastChange,
  canUndo,
  canRedo
} from '../services/versioning';

const STORAGE_KEY = 'budget_app_data';

export interface StorageData {
  budgets: Budget[];
  defaultBudgetId: number | null;
}

const getStorageData = (): StorageData => {
  const data = localStorage.getItem(STORAGE_KEY);
  try {
    if (!data) {
      return { budgets: [], defaultBudgetId: null };
    }

    const parsedData = JSON.parse(data);

    // Ensure we have a valid structure
    if (!parsedData || typeof parsedData !== 'object') {
      console.warn('Invalid data structure in localStorage, returning empty data');
      return { budgets: [], defaultBudgetId: null };
    }

    // Ensure budgets is an array
    if (!Array.isArray(parsedData.budgets)) {
      console.warn('Budgets is not an array in localStorage, initializing empty array');
      parsedData.budgets = [];
    }

    // Process each budget to ensure dates and settings are properly handled
    const processedBudgets = parsedData.budgets.map((budget: Partial<Budget>) => {
      const safeSettings = budget.settings
        ? {
            contributionCalculationType:
              budget.settings.contributionCalculationType ??
              ContributionCalculationType.INCOME_RATIO,
            currency: budget.settings.currency ?? DEFAULT_CURRENCY,
          }
        : {
            contributionCalculationType: ContributionCalculationType.INCOME_RATIO,
            currency: DEFAULT_CURRENCY,
          };

      // Normalize transactions responsible to canonical 'common'
      const normalizedTransactions = (
        Array.isArray(budget.transactions) ? budget.transactions : []
      ).map((t) => ({
        ...t,
        responsible: normalizeResponsible(String(t.responsible)),
        destination: normalizeDestination(String(t.destination)),
      }));

      // Normalize responsibles: collapse any translated variants into single protected 'common'
      const originalResponsibles = Array.isArray(budget.responsibles) ? budget.responsibles : [];
      const responsiblesMap = new Map<string, ResponsibleItem>();
      originalResponsibles.forEach((resp) => {
        const normalizedId = normalizeResponsible(String(resp.id));
        if (normalizedId === 'common') {
          responsiblesMap.set('common', {
            id: 'common',
            name: i18n.t('defaults.responsibles.common'),
            isProtected: true,
          });
        } else {
          responsiblesMap.set(String(resp.id), {
            id: String(resp.id),
            name: resp.name,
            isProtected: resp.isProtected,
          });
        }
      });

      if (!responsiblesMap.has('common')) {
        responsiblesMap.set('common', {
          id: 'common',
          name: i18n.t('defaults.responsibles.common'),
          isProtected: true,
        });
      }

      const normalizedResponsibles = Array.from(responsiblesMap.values());

      // Normalize destinations: ensure canonical protected 'cash' exists and collapse variants
      const originalDestinations = Array.isArray(budget.destinations) ? budget.destinations : [];
      const destinationsMap = new Map<string, DestinationItem>();
      originalDestinations.forEach((dest) => {
        const normalizedId = normalizeDestination(String(dest.id));
        if (normalizedId === 'cash') {
          destinationsMap.set('cash', {
            id: 'cash',
            name: i18n.t('defaults.destinations.cash'),
            isProtected: true,
          });
        } else {
          destinationsMap.set(String(dest.id), {
            id: String(dest.id),
            name: dest.name,
            isProtected: dest.isProtected,
          });
        }
      });
      if (!destinationsMap.has('cash')) {
        destinationsMap.set('cash', {
          id: 'cash',
          name: i18n.t('defaults.destinations.cash'),
          isProtected: true,
        });
      }
      const normalizedDestinations = Array.from(destinationsMap.values());

      return {
        ...budget,
        date: budget.date ? new Date(budget.date) : new Date(),
        transactions: normalizedTransactions,
        categories: Array.isArray(budget.categories) ? budget.categories : [],
        responsibles: normalizedResponsibles,
        destinations: normalizedDestinations,
        settings: safeSettings,
      } as Budget;
    });

    return {
      budgets: processedBudgets,
      defaultBudgetId: parsedData.defaultBudgetId || null,
    };
  } catch (error) {
    console.error('Error parsing storage data:', error);
    return { budgets: [], defaultBudgetId: null };
  }
};

const setStorageData = (data: StorageData, eventDetail?: { changedBudgetId?: number; deleted?: boolean }): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

  // Trigger a custom event that React Query hooks can listen for
  // This will help React Query know when to refetch data
  try {
    window.dispatchEvent(
      new CustomEvent(BUDGET_DATA_UPDATED_EVENT, {
        detail: {
          timestamp: new Date(),
          hasData: data.budgets && data.budgets.length > 0,
          defaultBudgetId: data.defaultBudgetId,
          ...eventDetail,
        },
      })
    );
  } catch (error) {
    console.error('Error dispatching budget_app_data_updated event:', error);
  }
};

export const createBudget = (
  budget: Omit<Budget, 'id'>,
  defaults?: {
    categories: CategoryItem[];
    responsibles: ResponsibleItem[];
    destinations: DestinationItem[];
  }
): number => {
  const data = getStorageData();
  // Calculer le nouvel ID en fonction des IDs existants
  const newId = data.budgets.length > 0 ? Math.max(...data.budgets.map((b) => b.id || 0)) + 1 : 1;

  // Ensure the budget has categories and responsibles
  const newBudget = {
    ...budget,
    id: newId,
    date: new Date(),
    // If categories are not provided, use localized default categories
    categories: budget.categories.length > 0 ? budget.categories : (defaults?.categories ?? []),
    // If responsibles are not provided, use localized default responsibles
    responsibles:
      budget.responsibles.length > 0 ? budget.responsibles : (defaults?.responsibles ?? []),
    // If destinations are not provided, use localized default destinations
    destinations:
      budget.destinations.length > 0 ? budget.destinations : (defaults?.destinations ?? []),
    settings: budget.settings ?? {
      contributionCalculationType: ContributionCalculationType.INCOME_RATIO,
      currency: DEFAULT_CURRENCY,
    },
  };

  data.budgets.push(newBudget);

  // Un nouveau budget devient automatiquement le budget par défaut
  data.defaultBudgetId = newId;

  setStorageData(data, { changedBudgetId: newId });
  
  // Initialize versioning for the new budget
  initializeVersioning(newBudget);
  
  return newId;
};

export const getBudget = (id: number): Budget | null => {
  // First check if we have a versioned version of this budget
  const versionedBudget = getCurrentVersion(id);
  if (versionedBudget) {
    return versionedBudget;
  }
  
  // Fallback to regular storage
  const data = getStorageData();
  const budget = data.budgets.find((b) => b.id === id) || null;
  
  // If budget exists but no versioning, initialize it (for existing users)
  if (budget) {
    initializeVersioning(budget);
    // Now return the versioned budget
    return getCurrentVersion(id) || budget;
  }
  
  return null;
};

export const getAllBudgets = (): Budget[] => {
  const data = getStorageData();
  return data.budgets;
};

export const updateBudget = (budget: Budget): void => {
  const data = getStorageData();
  data.budgets = data.budgets.map((b) => (b.id === budget.id ? budget : b));

  // Create a new version when budget is updated (before dispatching events)
  createVersion(budget, 'Budget updated');

  setStorageData(data, { changedBudgetId: budget.id });
};

export const duplicateBudget = (id: number, newName: string): number => {
  const data = getStorageData();
  const budget = data.budgets.find((b) => b.id === id);

  if (!budget) return 0;

  return createBudget({
    ...budget,
    name: newName,
    date: new Date(),
  });
};

/**
 * Returns the default budget (or the latest budget if no default is set)
 * This is the main function used by the Dashboard to display the current budget
 *
 * The function first tries to find the budget marked as default (using defaultBudgetId)
 * If no default budget is found or if it doesn't exist anymore, it returns the most recent budget
 */
export const getLatestBudget = (): Budget | null => {
  const data = getStorageData();
  if (data.budgets.length === 0) return null;

  // Chercher d'abord le budget par défaut
  if (data.defaultBudgetId) {
    const defaultBudget = data.budgets.find((b) => b.id === data.defaultBudgetId);
    if (defaultBudget) return defaultBudget;
  }

  // Sinon, retourner le plus récent
  return data.budgets.reduce((latest, current) => {
    const latestDate = new Date(latest.date);
    const currentDate = new Date(current.date);
    return currentDate > latestDate ? current : latest;
  });
};

export const deleteBudget = (id: number): void => {
  const data = getStorageData();
  data.budgets = data.budgets.filter((b) => b.id !== id);

  // If we're deleting the default budget
  if (data.defaultBudgetId === id) {
    // If there are no budgets left, set defaultBudgetId to null
    if (data.budgets.length === 0) {
      data.defaultBudgetId = null;
    } else {
      // Otherwise, set the most recent budget as the default
      const latestBudget = data.budgets.reduce((latest, current) => {
        const latestDate = new Date(latest.date);
        const currentDate = new Date(current.date);
        return currentDate > latestDate ? current : latest;
      }, data.budgets[0]);

      data.defaultBudgetId = latestBudget?.id ?? null;
    }
  }

  setStorageData(data, { changedBudgetId: id, deleted: true });

  // Clean up versioning data for deleted budget
  cleanupVersioningForBudget(id);
};

export const setDefaultBudget = (budgetId: number): void => {
  const data = getStorageData();
  // Vérifier que le budget existe
  if (data.budgets.some((b) => b.id === budgetId)) {
    data.defaultBudgetId = budgetId;
    setStorageData(data);
  }
};

/**
 * Returns just the ID of the default budget
 * This is used by getLatestBudget and for UI elements that need to know
 * which budget is the default (like star icons in the budget list)
 */
export const getDefaultBudgetId = (): number | null => {
  const data = getStorageData();
  return data.defaultBudgetId;
};

// Version navigation functions
export const navigateToPreviousVersion = (budgetId: number): Budget | null => {
  const previousBudget = goToPreviousVersion(budgetId);
  if (previousBudget) {
    // Emit events to trigger React Query invalidation
    window.dispatchEvent(
      new CustomEvent(BUDGET_DATA_UPDATED_EVENT, {
        detail: {
          timestamp: new Date(),
          budgetId,
          versionNavigation: true,
        },
      })
    );
  }
  return previousBudget;
};

export const navigateToNextVersion = (budgetId: number): Budget | null => {
  const nextBudget = goToNextVersion(budgetId);
  if (nextBudget) {
    // Emit events to trigger React Query invalidation
    window.dispatchEvent(
      new CustomEvent(BUDGET_DATA_UPDATED_EVENT, {
        detail: {
          timestamp: new Date(),
          budgetId,
          versionNavigation: true,
        },
      })
    );
  }
  return nextBudget;
};

// Undo/Redo functions
export const undo = (budgetId: number): Budget | null => {
  const previousBudget = undoLastChange(budgetId);
  if (previousBudget) {
    // Emit events to trigger React Query invalidation
    window.dispatchEvent(
      new CustomEvent(BUDGET_DATA_UPDATED_EVENT, {
        detail: {
          timestamp: new Date(),
          budgetId,
          undo: true,
        },
      })
    );
  }
  return previousBudget;
};

export const redo = (budgetId: number): Budget | null => {
  const nextBudget = redoLastChange(budgetId);
  if (nextBudget) {
    // Emit events to trigger React Query invalidation
    window.dispatchEvent(
      new CustomEvent(BUDGET_DATA_UPDATED_EVENT, {
        detail: {
          timestamp: new Date(),
          budgetId,
          redo: true,
        },
      })
    );
  }
  return nextBudget;
};

// Export version navigation info and undo/redo capabilities
export { getVersionNavigationInfo, isViewingHistoricalVersion, canUndo, canRedo };
